import { useCallback, useEffect, useState } from 'react';
import { Check, ChevronLeft, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { cn } from '../lib/utils';
import { MobileHeader } from '../components/MobileHeader';
import { copy, EntryForm } from '../components/memo/EntryList';

const both = (m) => `🇫🇷 ${m.text}\n\n🇬🇧 ${m.text_en}`;
const Chip = ({ children, ...p }) => (
  <button type="button" {...p} className="rounded-lg border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-bold text-[#0288D1] hover:bg-[#0288D1]/10">{children}</button>
);

/** Une ligne du panneau de gauche : titre (ouvre), renommer sur place, copier FR / EN / FR+EN. */
function Row({ m, active, onOpen, onRename }) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(m.title);
  useEffect(() => setTitle(m.title), [m.title]);
  const done = () => { setRenaming(false); if (title.trim() && title.trim() !== m.title) onRename(title.trim()); else setTitle(m.title); };
  return (
    <div className={cn('rounded-xl px-2.5 py-2 space-y-1.5', active ? 'bg-[#0288D1]/10' : 'hover:bg-slate-50')} data-testid="msg-row">
      {renaming ? (
        <div className="flex items-center gap-1">
          <input value={title} onChange={e => setTitle(e.target.value)} autoFocus data-testid="msg-rename-input"
            onKeyDown={e => { if (e.key === 'Enter') done(); if (e.key === 'Escape') { setTitle(m.title); setRenaming(false); } }}
            className="flex-1 rounded-lg border border-slate-300 px-2 py-1 text-base" />
          <button type="button" onClick={done} className="text-[#26A69A]" aria-label="Valider" data-testid="msg-rename-ok"><Check size={16} /></button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <button type="button" onClick={onOpen} className="flex-1 text-left text-sm font-semibold text-slate-800 leading-tight" data-testid="msg-open">{m.title}</button>
          <button type="button" onClick={() => setRenaming(true)} className="text-slate-300 hover:text-slate-600" aria-label="Renommer" data-testid="msg-rename"><Pencil size={13} /></button>
        </div>
      )}
      <div className="flex gap-1">
        <Chip onClick={() => copy(m.text, `« ${m.title} » (FR)`)} data-testid="msg-copy-fr">FR</Chip>
        {m.text_en && <Chip onClick={() => copy(m.text_en, `« ${m.title} » (EN)`)}>EN</Chip>}
        {m.text_en && <Chip onClick={() => copy(both(m), `« ${m.title} » (FR+EN)`)}>FR+EN</Chip>}
      </div>
    </div>
  );
}

export default function MessagesTypes() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);  // id
  const [mode, setMode] = useState('view');  // view | edit | new
  const [confirmDel, setConfirmDel] = useState(false);
  const load = useCallback(() => api.get('/memo/messages-types').then(setItems).catch(e => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const low = q.trim().toLowerCase();
  const shown = (items || []).filter(m => !low || `${m.title} ${m.text} ${m.text_en || ''}`.toLowerCase().includes(low));
  const current = (items || []).find(m => m.id === selected) || null;
  const desktopCurrent = current || (mode === 'view' ? shown[0] : null);

  const put = async (m, changes) => {
    try { await api.put(`/memo/messages-types/${m.id}`, { title: m.title, text: m.text, text_en: m.text_en, ...changes }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const save = async (body) => {
    try {
      if (mode === 'new') { const m = await api.post('/memo/messages-types', body); setSelected(m.id); }
      else await api.put(`/memo/messages-types/${desktopCurrent.id}`, body);
      setMode('view'); load();
    } catch (e) { toast.error(e.message); }
  };
  const remove = async () => {
    try { await api.delete(`/memo/messages-types/${desktopCurrent.id}`); setSelected(null); setConfirmDel(false); load(); }
    catch (e) { toast.error(e.message); }
  };
  const importGoogle = async () => {
    try { const r = await api.post('/memo-import/google'); toast.success(`Repris de Google : ${Object.values(r.ajoutes).reduce((a, b) => a + b, 0)} élément(s)`); load(); }
    catch (e) { toast.error(e.message); }
  };
  const open = (id) => { setSelected(id); setMode('view'); setConfirmDel(false); };
  const showDetail = selected !== null || mode === 'new';  // téléphone : liste OU détail

  return (
    <div className="max-w-7xl mx-auto pb-28 lg:pb-6" data-testid="messages-types-page">
      <MobileHeader title="Messages types" />
      <div className="lg:grid lg:grid-cols-[minmax(240px,1fr)_minmax(0,3fr)] lg:gap-5">
        {/* Panneau de gauche : vue d'ensemble */}
        <aside className={cn('bg-white rounded-2xl border border-slate-100 shadow-sm p-3 space-y-2 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto',
          showDetail && 'hidden lg:block')} data-testid="msg-panel">
          <div className="flex gap-2">
            <div className="flex-1 flex items-center gap-1.5 rounded-xl border border-slate-200 px-2">
              <Search size={14} className="text-slate-400" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Chercher…" className="flex-1 py-1.5 text-base outline-none bg-transparent min-w-0" />
            </div>
            <button type="button" onClick={() => { setMode('new'); setSelected(null); }} data-testid="entry-add"
              className="rounded-xl bg-[#26A69A] text-white px-2.5" aria-label="Nouveau message"><Plus size={16} /></button>
          </div>
          {items === null && <p className="text-sm text-slate-400 px-1">…</p>}
          {items?.length === 0 && (
            <button type="button" onClick={importGoogle} className="w-full rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-[#0288D1]">
              Reprendre mes messages depuis Google Tasks</button>
          )}
          {shown.map(m => (
            <Row key={m.id} m={m} active={desktopCurrent?.id === m.id && mode !== 'new'} onOpen={() => open(m.id)}
              onRename={(title) => put(m, { title })} />
          ))}
        </aside>

        {/* Détail : le message complet */}
        <section className={cn('space-y-3', !showDetail && 'hidden lg:block')}>
          <button type="button" onClick={() => { setSelected(null); setMode('view'); }} className="lg:hidden flex items-center gap-1 text-sm font-semibold text-slate-500">
            <ChevronLeft size={16} /> Liste</button>
          {mode === 'new' && <EntryForm withEn onSave={save} onCancel={() => setMode('view')} />}
          {mode === 'edit' && desktopCurrent && <EntryForm initial={desktopCurrent} withEn onSave={save} onCancel={() => setMode('view')} />}
          {mode === 'view' && desktopCurrent && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-3" data-testid="msg-detail">
              <div className="flex items-start gap-2">
                <h2 className="text-xl font-extrabold text-slate-900 font-manrope flex-1">{desktopCurrent.title}</h2>
                <button type="button" onClick={() => setMode('edit')} className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 flex items-center gap-1"><Pencil size={14} /> Modifier</button>
                <button type="button" onClick={() => setConfirmDel(true)} className="rounded-xl border border-slate-200 px-2 py-1.5 text-slate-400 hover:text-red-600" aria-label="Supprimer"><Trash2 size={15} /></button>
              </div>
              {confirmDel && (
                <div className="flex items-center gap-2 text-sm bg-red-50 rounded-xl px-3 py-2">Supprimer ce message ?
                  <button type="button" onClick={remove} className="ml-auto font-bold text-red-600">Supprimer</button>
                  <button type="button" onClick={() => setConfirmDel(false)} className="font-semibold text-slate-500">Non</button></div>
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => copy(desktopCurrent.text, 'Texte FR')} className="rounded-xl bg-[#0288D1] text-white text-sm font-semibold px-3 py-1.5">Copier FR</button>
                {desktopCurrent.text_en && <>
                  <button type="button" onClick={() => copy(desktopCurrent.text_en, 'Texte EN')} className="rounded-xl border border-[#0288D1] text-[#0288D1] text-sm font-semibold px-3 py-1.5">Copier EN</button>
                  <button type="button" onClick={() => copy(both(desktopCurrent), 'FR + EN')} className="rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold px-3 py-1.5">Copier FR + EN</button>
                </>}
              </div>
              <div className="text-xs font-bold text-slate-400 uppercase">🇫🇷 Français</div>
              <p className="text-sm text-slate-800 whitespace-pre-line">{desktopCurrent.text || '—'}</p>
              {desktopCurrent.text_en && <>
                <div className="text-xs font-bold text-slate-400 uppercase pt-2 border-t border-slate-100">🇬🇧 English</div>
                <p className="text-sm text-slate-700 whitespace-pre-line">{desktopCurrent.text_en}</p>
              </>}
            </div>
          )}
          {mode === 'view' && !desktopCurrent && items?.length > 0 && <p className="text-slate-400 text-sm">Choisis un message à gauche.</p>}
        </section>
      </div>
    </div>
  );
}
